import React, { useEffect, useRef } from 'react';
import { Button, Dropdown } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { isArray, isNil, uniqBy } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { getPayloadForLocalState } from 'routes/screens/contacts/screens/utils';
import {
  ColumnSettingsType,
  ConditionType,
  FilterViewType,
  FormServFieldType,
  RuleType,
  UserType,
} from '../../../shared/types';
import Icon from '../../../shared/components/Icon';
import {
  BACK_END_FIELDS,
  FieldBasedFilterOperations,
  FilterOperations,
  TEMPORARY_HIDDEN_FIELDS,
} from '../../../shared/constants';
import {
  FormActions,
  FormContainer,
  FormContent,
  FormField,
} from '../../../shared/styles';
import { AddFilters } from '../../styles';
import FilterCondition from './FilterCondition';
import { FilterSegment } from './FilterSegment';

type FiltersProps = {
  fields: FormServFieldType[];
  columnSettings: ColumnSettingsType;
  filter: any;
  hideFields?: string[];
  onFilter?: (filterData: any) => void;
  userInfo?: UserType;
  isFilterConditionsModified?: boolean;
  rule?: RuleType;
  onClearFilterView?: (selectedFilter?: FilterViewType) => void;
  selectedFilter?: FilterViewType;
};

const Filters = (props: FiltersProps) => {
  const { t } = useTranslation();
  const {
    fields,
    columnSettings,
    onFilter,
    filter,
    hideFields,
    userInfo,
    isFilterConditionsModified,
    rule,
    onClearFilterView,
    selectedFilter,
  } = props;

  const availableFilterFields = fields
    .filter(
      (field: FormServFieldType) =>
        ![
          ...TEMPORARY_HIDDEN_FIELDS,
          ...(hideFields || []),
          ...BACK_END_FIELDS,
        ].includes(field.name) && FieldBasedFilterOperations[field.type],
    )
    .map((field) => ({
      ...field,
      label: field.field_options?.fs_default
        ? `${t(
            `contacts.import_contact.default_fields.contact.${field?.name}`,
          )}`
        : field.label || '',
    }));

  const [showAddFilter, toggleAddFilter] = React.useState<boolean>(false);
  const [filterFields, setFilterFields] = React.useState<FormServFieldType[]>(
    [],
  );
  const filterSchema = {};
  filterFields.forEach((filterField) => {
    filterSchema[filterField.name] = Yup.mixed().test({
      test: function (condition: ConditionType, context) {
        const {
          createError,
        }: {
          createError: (params?: Yup.CreateErrorOptions) => Yup.ValidationError;
        } = context || {};
        if (
          condition?.operator &&
          FilterOperations[condition.operator]?.between &&
          FilterOperations[condition.operator]?.needValue
        ) {
          if (!condition.value)
            return createError({
              message: `${t('forms.err_msg.required_field')}`,
            });
          if (condition.value && !condition?.value?.from)
            return createError({
              path: `${filterField.name}.from`,
              message: `${t('forms.err_msg.from_required')}`,
            });
          if (condition.value && !condition?.value?.to)
            return createError({
              path: `${filterField.name}.to`,
              message: `${t('forms.err_msg.to_required')}`,
            });
        }
        if (
          condition?.operator &&
          FilterOperations[condition.operator]?.needValue &&
          (!condition.value ||
            (typeof condition.value === 'string' &&
              condition.value.trim() === '') ||
            (!isNil(condition.value) &&
              isArray(condition.value) &&
              condition.value.length === 0))
        ) {
          return createError({
            message: `${t('forms.err_msg.required_field')}`,
          });
        }
        return true;
      },
    });
  });

  const form = useForm({
    resolver: yupResolver(Yup.object(filterSchema).required()),
    defaultValues: filter,
  });
  const { register, handleSubmit, reset, getValues, watch, setValue } = form;
  const setFormValue = (
    fieldId: string,
    fieldValue: ConditionType | undefined,
  ) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };
  const onSubmit = (data: any) => {
    onFilter?.(data);
  };

  useEffect(() => {
    reset(filter);
  }, [filter]);

  const filterFieldIds = filterFields.map((filterField) => filterField.name);

  React.useEffect(() => {
    const values = getValues();
    const permanentFilters = availableFilterFields.filter(
      (field: FormServFieldType) =>
        columnSettings[field.name]?.filterable ||
        field?.field_options?.filterable,
    );
    const selectedFilters = availableFilterFields.filter(
      (field: FormServFieldType) =>
        (filter && !!filter[field.name]) || (values && !!values[field.name]),
    );
    // To maintain the order of filters
    setFilterFields(
      uniqBy(
        [...permanentFilters, ...selectedFilters],
        (field: FormServFieldType) => field.name,
      ),
    );
  }, [fields, filter]);

  React.useEffect(() => {
    if (!hideFields) return;
    hideFields.forEach((hideField) => setValue(hideField, undefined));
  }, [hideFields]);

  const filtersRef = useRef<HTMLDivElement>(null);

  return (
    <FormContainer
      noValidate
      onSubmit={handleSubmit(onSubmit, (err) => console.log('ERR', err))}
    >
      <FilterSegment
        rule={rule}
        isFilterConditionsModified={isFilterConditionsModified}
      />
      <FormContent pd="24px 12px 0px" ref={filtersRef}>
        {filterFields.map((field: FormServFieldType) => {
          register(field.name);
          return (
            <React.Fragment key={field.name}>
              <FilterCondition
                field={field}
                form={form}
                value={watch(field.name)}
                onChange={(val: ConditionType | undefined) => {
                  setFormValue(field.name, val);
                  setTimeout(() => {
                    if (filtersRef?.current?.scrollTop) {
                      filtersRef.current.scrollTop =
                        filtersRef.current.scrollHeight;
                    }
                  }, 200);
                }}
                columnSetting={columnSettings[field.name]}
                userInfo={userInfo}
              />
            </React.Fragment>
          );
        })}
        {showAddFilter ? (
          <FormField className="add-filter">
            <Dropdown
              {...{
                defaultIsOpen: true,
                onOuterClick: () => toggleAddFilter(false),
              }}
              type="input"
              filterKey="label"
              id="add-filter"
              itemComponent={(v: FormServFieldType) => v.label}
              itemToString={(v: FormServFieldType) => v.label || ''}
              defaultSelectedItem={null}
              placeholder={`${t('forms.placeholders.choose_contact_field')}`}
              items={availableFilterFields.filter(
                (field: FormServFieldType) =>
                  !filterFieldIds.includes(field.name),
              )}
              emptyItemsText={`${t('no_results_found')}`}
              onChange={(option: FormServFieldType) => {
                if (!option) return;
                const newFilterFields = [...filterFields];
                newFilterFields.push(option);
                setFilterFields(newFilterFields);
                toggleAddFilter(false);
              }}
            />
          </FormField>
        ) : (
          <FormField className="bottom-sticky">
            <AddFilters onClick={() => toggleAddFilter(true)}>
              <Icon glyph="plus" width="16" height="16" />
              &nbsp;&nbsp;{t('add_filter')}
            </AddFilters>
          </FormField>
        )}
      </FormContent>
      {
        // these are temporary once the fremium-v2 merged will be removed
      }
      <FormActions style={{ justifyContent: 'flex-end', zIndex: 701 }}>
        <div className="contact-button-style">
          <Button
            inline
            htmlType="reset"
            type="secondary"
            onClick={() => {
              const newFormValues = getPayloadForLocalState(selectedFilter);
              reset(newFormValues);
              onClearFilterView?.(selectedFilter);
            }}
          >
            {t('clear')}
          </Button>
          <Button inline htmlType="submit">
            {t('apply')}
          </Button>
        </div>
      </FormActions>
    </FormContainer>
  );
};

export default Filters;
